/**
 * Fabryki kluczy do TanStack Query.
 * Zasób (np. 'users') + companyId (tenant) dla danych zależnych od firmy.
 */

export const companyKeys = {
  all: ['company'] as const,
  main: () => [...companyKeys.all, 'main'] as const,
  contactAndSupports: (companyId: string) =>
    [...companyKeys.all, 'contactAndSupports', companyId] as const
};

export const accessRangeForRolesKeys = {
  all: (companyId: string) =>
    ['access_range_for_roles', companyId] as const,
  list: (companyId: string) =>
    [...accessRangeForRolesKeys.all(companyId), 'list'] as const
};

export const companiesKeys = {
  mainList: () => ['companies', 'mainList'] as const
};

export const platformCompaniesKeys = {
  all: () => ['platform_companies'] as const,
  detail: (id: string) => ['platform_companies', id] as const
};

export const platformUsersKeys = {
  all: () => ['platform_users'] as const
};

export const usersKeys = {
  all: (companyId: string) => ['users', companyId] as const,
  list: (companyId: string) => ['users', companyId] as const,
  detail: (companyId: string, id: string) => ['users', companyId, id] as const
};

export const areasKeys = {
  all: (companyId: string) => ['areas', companyId] as const,
  list: (companyId: string) => ['areas', companyId] as const,
  detail: (companyId: string, id: string) => ['areas', companyId, id] as const
};

/** filters: { area_ids: number[] } – serializowane do klucza */
export const assessmentsKeys = {
  all: (companyId: string) => ['assessments', companyId] as const,
  list: (companyId: string, filters: { area_ids?: number[] }) =>
    ['assessments', companyId, filters] as const
};

export const methodologiesKeys = {
  all: () => ['methodologies'] as const
};

export const practicesKeys = {
  all: (methodologyIds?: number[]) => ['practices', methodologyIds] as const
};

/** filters + page, perPage dla paginacji */
export const auditsKeys = {
  all: (companyId: string) => ['audits', companyId] as const,
  list: (
    companyId: string,
    filters: { area_ids?: number[] },
    page?: number,
    perPage?: number
  ) => ['audits', companyId, filters, page, perPage] as const
};

export const auditFormsKeys = {
  all: (companyId: string) => ['audit_forms', companyId] as const,
  list: (companyId: string, filters?: { states?: string[] }) =>
    ['audit_forms', companyId, filters] as const
};

export const teamsKeys = {
  all: (companyId: string) => ['teams', companyId] as const,
  list: (companyId: string) => ['teams', companyId] as const,
  detail: (companyId: string, id: string) => ['teams', companyId, id] as const
};

export const taskTypesKeys = {
  all: (companyId: string) => ['task_types', companyId] as const,
  list: (companyId: string) => ['task_types', companyId] as const
};

export const priorityLevelsKeys = {
  all: () => ['priority_levels'] as const
};

export const languagesKeys = {
  all: () => ['languages'] as const,
  list: () => ['languages'] as const
};

export const localesKeys = {
  all: (languageCodes?: string[]) =>
    languageCodes?.length ? ['locales', languageCodes] as const : (['locales'] as const)
};

export const dateFormatsKeys = {
  all: () => ['date_formats'] as const,
  list: () => ['date_formats'] as const
};

export const executionProgressTrackingsKeys = {
  all: (companyId: string) => ['execution_progress_trackings', companyId] as const,
  list: (companyId: string) => ['execution_progress_trackings', companyId] as const
};

export const userFunctionsKeys = {
  all: (companyId: string) => ['user_functions', companyId] as const,
  list: (companyId: string) => ['user_functions', companyId] as const
};

/** filters: ITasksRequest.filters */
export const tasksKeys = {
  all: (companyId: string) => ['tasks', companyId] as const,
  list: (companyId: string, filters: Record<string, unknown>) =>
    ['tasks', companyId, filters] as const,
  detail: (companyId: string, id: string | number) =>
    ['tasks', companyId, id] as const
};

export const meKeys = {
  all: () => ['me'] as const
};

export const tenantDatabaseKeys = {
  all: () => ['tenant_database'] as const,
  detail: (id: string) => ['tenant_database', id] as const
};
